/**
 * Decorative animated zodiac ring for the hero section.
 * Three concentric rings with the 12 zodiac glyphs and 12 spoke divisions.
 * Slow CCW rotation. Designed to feel like a luminous astrological wheel.
 */

const SIGN_GLYPHS = ["♈\uFE0E","♉\uFE0E","♊\uFE0E","♋\uFE0E","♌\uFE0E","♍\uFE0E","♎\uFE0E","♏\uFE0E","♐\uFE0E","♑\uFE0E","♒\uFE0E","♓\uFE0E"];

export default function ZodiacRing() {
  const size = 720;            // SVG canvas size in user units
  const c = size / 2;
  const rOuter = 340;
  const rGlyphBand = 305;
  const rMid = 240;
  const rInner = 160;
  const rCore = 70;
  const rDots = 90;

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {/* Soft radial glow behind the wheel */}
      <div
        className="absolute"
        style={{
          width: "85vmin", height: "85vmin",
          background: "radial-gradient(circle, rgba(58,34,73,0.55) 0%, rgba(201,162,94,0.06) 40%, transparent 72%)",
          filter: "blur(28px)",
        }}
      />

      {/* Outer slow-rotating wheel with glyphs + spokes */}
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="zodiac-ring-spin"
        style={{ width: "108vmin", height: "108vmin", maxWidth: "980px", maxHeight: "980px" }}
      >
        <defs>
          <radialGradient id="zg-vignette" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3A2249" stopOpacity="0.35" />
            <stop offset="60%" stopColor="#3A2249" stopOpacity="0.0" />
          </radialGradient>
          <linearGradient id="zg-spoke" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(201,162,94,0)" />
            <stop offset="50%" stopColor="rgba(201,162,94,0.65)" />
            <stop offset="100%" stopColor="rgba(201,162,94,0)" />
          </linearGradient>
        </defs>

        <circle cx={c} cy={c} r={rOuter * 1.05} fill="url(#zg-vignette)" />

        {/* Rings */}
        <circle cx={c} cy={c} r={rOuter} fill="none" stroke="rgba(201,162,94,0.55)" strokeWidth="1.2" />
        <circle cx={c} cy={c} r={rOuter - 30} fill="none" stroke="rgba(201,162,94,0.22)" strokeWidth="0.8" />
        <circle cx={c} cy={c} r={rMid} fill="none" stroke="rgba(201,162,94,0.30)" strokeWidth="1" />
        <circle cx={c} cy={c} r={rInner} fill="none" stroke="rgba(201,162,94,0.30)" strokeWidth="0.8" strokeDasharray="2 6" />
        <circle cx={c} cy={c} r={rCore} fill="none" stroke="rgba(201,162,94,0.45)" strokeWidth="0.8" />

        {/* 12 spokes (dim) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * 30 - 90) * Math.PI / 180;
          const x1 = c + rInner * Math.cos(a);
          const y1 = c + rInner * Math.sin(a);
          const x2 = c + rOuter * Math.cos(a);
          const y2 = c + rOuter * Math.sin(a);
          return <line key={`spoke-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="rgba(201,162,94,0.20)" strokeWidth="0.8" />;
        })}

        {/* 4 cardinal cross axes (gold, brighter) */}
        {[0, 90, 180, 270].map((deg) => {
          const a = (deg - 90) * Math.PI / 180;
          const x1 = c + rCore * Math.cos(a);
          const y1 = c + rCore * Math.sin(a);
          const x2 = c + (rOuter + 20) * Math.cos(a);
          const y2 = c + (rOuter + 20) * Math.sin(a);
          return <line key={`cross-${deg}`} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="url(#zg-spoke)" strokeWidth="1" />;
        })}

        {/* Sign glyphs around the band */}
        {SIGN_GLYPHS.map((g, i) => {
          // Counter-clockwise from the left (Ascendant): Aries, Tauro, Géminis…
          const a = (180 - (i * 30 + 15)) * Math.PI / 180;
          const x = c + rGlyphBand * Math.cos(a);
          const y = c + rGlyphBand * Math.sin(a);
          return (
            <text key={`g-${i}`} x={x} y={y + 9}
              textAnchor="middle"
              fill="#C9A25E"
              fontFamily="Cormorant Garamond, serif"
              fontSize="28"
              opacity="0.85"
              className="zodiac-glyph-counter"
              style={{ transformOrigin: `${x}px ${y}px` }}>
              {g}
            </text>
          );
        })}

        {/* Twinkling dots scattered around the inner ring */}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * 15 + 7) * Math.PI / 180;
          const x = c + (rDots + (i % 3) * 12) * Math.cos(a);
          const y = c + (rDots + (i % 3) * 12) * Math.sin(a);
          const r = i % 4 === 0 ? 2.2 : 1.2;
          return (
            <circle key={`d-${i}`} cx={x} cy={y} r={r}
              fill={i % 5 === 0 ? "#C9A25E" : "#B79BC7"}
              opacity="0.75"
              className="twinkle"
              style={{ animationDelay: `${(i * 137) % 3000}ms` }}
            />
          );
        })}

        {/* Center sigil */}
        <circle cx={c} cy={c} r="4" fill="#C9A25E" opacity="0.9" />
        <circle cx={c} cy={c} r="14" fill="none" stroke="rgba(201,162,94,0.45)" strokeWidth="0.6" />
      </svg>

      {/* Counter-rotating ring (gold dashes) to add depth */}
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="zodiac-ring-counter absolute"
        style={{ width: "84vmin", height: "84vmin", maxWidth: "760px", maxHeight: "760px" }}
      >
        <circle cx={c} cy={c} r="300" fill="none" stroke="rgba(201,162,94,0.38)" strokeWidth="0.7" strokeDasharray="1 12" />
        <circle cx={c} cy={c} r="270" fill="none" stroke="rgba(58,34,73,0.35)" strokeWidth="0.6" />
      </svg>
    </div>
  );
}
